// An organisation's business details — its legal name, PAN and registered address — as its owner keeps
// them (ADR-0046; spec/04 §2, spec/13 §13, spec/06 §10.3).
//
//   GET   /v1/organizations/{o}/business-details   read them                     settings:all
//   PATCH /v1/organizations/{o}/business-details   complete or correct them      settings:all
//
// HQ invites a café with its business name and its owner's email or phone alone (ADR-0046, the product
// owner's decision, 2026-10-08), so these may be missing until the owner completes them during setup —
// HQ may have prefilled any of them. They are the organisation record's own fields (`auth.organization`,
// spec/04 §2), the same ones HQ reads on its record (`HqOrganization`); this is the owner's door to them.
//
// **What waits on them.** A GST document — a tax invoice or a bill of supply — is issued under the
// organisation's legal name and registered address, so until both are recorded no venue of the
// organisation records a `regular` or `composition` tax registration (`409 business-details-required`,
// spec/06 §10.3). A venue that is `unregistered` sells with plain bills from its first sale, as it always
// has, and needs neither.
//
// **Who.** `settings:all` — the owner's organisation-configuration permission (spec/07 §6.3); nothing is
// widened. Every change is one audit row in the organisation's log (`org.business_details.update`), with
// what changed before and after: the PAN masked to its last four, the legal name to its first letter and
// the registered address whole, as the audit service masks them (spec/07 §11.1). The trade name and the
// slug are HQ's record, read here and changed only by HQ.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { SETTINGS_ALL } from "../permissions/index.js";
import { AddressSchema } from "../primitives/address.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { PanSchema } from "./hq-organizations.js";
export const OrganizationBusinessDetailsSchema = z
    .object({
    /** The trade name, as HQ recorded it. Read only here. */
    name: z.string(),
    /** The registered legal entity; `null` until it is recorded. */
    legalName: z.string().nullable(),
    /** `null` until it is recorded. */
    pan: z.string().nullable(),
    /** `null` until it is recorded. */
    registeredAddress: AddressSchema.nullable(),
    /**
     * The legal name and the registered address are both recorded — what a venue's GST registration, and so
     * a GST document, needs. The PAN is not part of it: an organisation may have none to hand.
     */
    complete: z.boolean(),
})
    .openapi('OrganizationBusinessDetails', {
    description: 'The organisation’s legal name, PAN and registered address, and whether a GST registration may rely on them.',
});
export const UpdateOrganizationBusinessDetailsSchema = z
    .object({
    /** The registered legal entity. Changed, never cleared: a venue's GST registration relies on it. */
    legalName: z.string().trim().min(1).max(200).optional(),
    /** A PAN, or `null` to clear one recorded by mistake. */
    pan: PanSchema.nullable().optional(),
    /** The registered address. Changed, never cleared. */
    registeredAddress: AddressSchema.optional(),
})
    .strict()
    .refine((body) => body.legalName !== undefined || body.pan !== undefined || body.registeredAddress !== undefined, {
    message: 'Give at least one of legalName, pan and registeredAddress.',
    path: ['legalName'],
})
    .openapi('UpdateOrganizationBusinessDetails', { description: 'The business details to record; what is left out is kept.' });
const notFound = problemResponse('No such organisation, or you are not on its staff.', 'not-found');
export const getOrganizationBusinessDetailsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/business-details',
    operationId: 'getOrganizationBusinessDetails',
    tags: ['organizations'],
    summary: 'The organisation’s legal name, PAN and registered address',
    description: 'What a venue’s GST documents are issued under, and whether they are complete (ADR-0046).',
    [PERMISSION_EXTENSION]: SETTINGS_ALL,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(OrganizationBusinessDetailsSchema, 'The business details.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateOrganizationBusinessDetailsRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/business-details',
    operationId: 'updateOrganizationBusinessDetails',
    tags: ['organizations'],
    summary: 'Complete or correct the organisation’s business details',
    description: 'Records the legal name, PAN and registered address given; what is left out is kept (ADR-0046). The legal name ' +
        'and the registered address are changed, never cleared; a PAN may be cleared with `null`. A change that changes ' +
        'nothing writes nothing. One audit row in the organisation’s log, before and after.',
    [PERMISSION_EXTENSION]: SETTINGS_ALL,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: UpdateOrganizationBusinessDetailsSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(OrganizationBusinessDetailsSchema, 'The business details as they are now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const BUSINESS_DETAILS_ROUTES = [getOrganizationBusinessDetailsRoute, updateOrganizationBusinessDetailsRoute];
//# sourceMappingURL=business-details.js.map